import React, { useLayoutEffect, useRef, useState } from 'react';

interface Size {
  height: number;
  width: number;
}

interface Props {
  children: (size: Size) => React.ReactNode;
}

/**
 * Hands its child the size of the parent element, and again whenever that
 * changes. A drop-in for react-virtualized's AutoSizer, which was the one
 * thing still imported from that package; it measured the parent the same
 * way, minus padding, through a zero-sized marker element.
 *
 * The child renders on the very first pass, at 0×0, and is resized once the
 * parent has been measured - the same order as the original. That order is
 * load-bearing: a list mounted in the first commit has its ref set by the
 * time the parent's effects run, and `playlistComponent`'s locate-on-arrival
 * effect gives up if the list ref is still empty.
 */
export default function AutoSizer({ children }: Props) {
  const marker = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ height: 0, width: 0 });

  useLayoutEffect(function observeParent() {
    const parent = marker.current?.parentElement;

    if (!parent) {
      return;
    }

    function measure() {
      const style = window.getComputedStyle(parent!);
      const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const next = {
        height: Math.max(0, parent!.offsetHeight - paddingY),
        width: Math.max(0, parent!.offsetWidth - paddingX),
      };

      setSize((current) =>
        current.height === next.height && current.width === next.width ? current : next,
      );
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(parent);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={marker} style={{ overflow: 'visible', height: 0, width: 0 }}>
      {children(size)}
    </div>
  );
}
